import { clamp } from "@foretagsplatsen/core-helpers/number";
import { Widget } from "@foretagsplatsen/widgetjs";
import ChartWidget from "../../../../core/widgets/ChartWidget/ChartWidget.js";
import CssClassNameVisitor from "../../../../models/reports/reports/CssClassNameVisitor.js";
import ForecastFiscalYearCard from "../../../../core/widgets/ForecastFiscalYearCard/ForecastFiscalYearCard.js";
import keyFigureValueFormatter from "../../../../models/keyFigure/rendering/keyFigureValueFormatter.js";
import ReportTableWidget from "../../../reports2/ReportTableWidget/ReportTableWidget.js";

export const safetyScrollMargin = 5;
const stickyFirstRowCSSClass = "sticky-first-row";

/**
 * A report viewer showing a forecast report table. The widget renders
 * a title card, a chart and a summary row in the table head.
 * Additionally, the widget renders a settings button above the report
 * table.
 *
 * @param {AbstractForecastReport} report - The forecast report
 * @param {function({row,column}): void} onCellClick - Called with the
 *   coordinates of the cell that has been clicked
 * @param {boolean} saveSpaceMode - When true, we will render less
 *   info around the report to maximize the number of visible rows
 */
export default class ForecastReportViewerWidget extends Widget {
	constructor({ report, onCellClick, saveSpaceMode }) {
		super(...arguments);

		this._report = report;
		this._onCellClick = onCellClick;
		this._saveSpaceMode = saveSpaceMode;

		this._selectedCell = {};
		this._cssClassNameRenderer = new CssClassNameVisitor();

		this._currentFiscalYearCard = null;

		this._reportViewerWidget = this._makeReportViewer();

		this._reportViewerWidget.onAttach.register(
			this.adjustReportTable.bind(this),
		);
	}

	setSaveSpaceMode(boolean) {
		this._saveSpaceMode = boolean;
		this.update();
	}

	adjustReportTable() {
		this._makeFirstRowSticky();
		this._registerCardUpdateOnScroll();
	}

	selectCell(cell) {
		this._selectedCell = cell;

		// Only update the previously selected cell and the newly
		// selected cell to avoid refreshing the whole report. This is
		// even more important when the report includes a chart
		// because updating the chart would animate it again.
		this.asJQuery().find("td.selected").removeClass("selected");

		this.asJQuery()
			.find(
				`td.clickable[data-row-id=${cell.row.getId()}][data-column-id=${cell.column.getId()}]`,
			)
			.addClass("selected");
	}

	/**
	 * Scroll to the currently selected cell to make it visible.
	 * `leftShift` and `rightShift` represent an amount of pixels
	 * of the report viewer that is covered by something else (e.g., a
	 * sticky column).
	 *
	 * @param {integer} [leftShift] - Indicate the width of the left
	 *   part of the report viewer the selected cell shouldn't appear
	 *   in. This is for example used when the first column is sticky:
	 *   the selected cell should appear on the right of the sticky
	 *   column to not be hidden by it
	 * @param {integer} [rightShift] - Same as `leftShift` but for
	 *   the right part of the report viewer
	 */
	scrollToSelectedCell({ leftShift = 0, rightShift = 0 } = {}) {
		const cell = this.asJQuery().find("td.selected");
		const reportViewerDOM = this.asJQuery().find(".reportViewer").get(0);

		if (!cell.position()) {
			return;
		}

		const scrollTarget = this._computeScrollTarget({
			cell,
			leftShift,
			rightShift,
			reportViewerDOM,
		});

		this.asJQuery().find(".reportViewer").animate(scrollTarget, 400);
	}

	getReport() {
		return this._report;
	}

	renderContentOn(html) {
		html.div(
			{ class: "ForecastReportViewerWidget" },
			html.div(
				{
					class: [
						"forecast-report",
						"reportViewer",
						"sticky-first-column",
						stickyFirstRowCSSClass,
					],
					"data-test": "forecast-report-viewer",
				},
				this._reportViewerWidget,
			),
		);
	}

	/**
	 * This code is all about injecting some dynamic CSS
	 * in order to make the first row sticky (in addition
	 * to the first column) which makes it quite trickier
	 * than expected.
	 */
	_makeFirstRowSticky() {
		const container = document.getElementsByClassName(
			stickyFirstRowCSSClass,
		)[0];

		if (!container) {
			return;
		}

		// Here we assume only one report is displayed per page
		const table = document.getElementsByTagName("table")[0];
		const thead = table?.childNodes[0];
		const tbody = table?.childNodes[1];

		/*
		 * We take the first cell of the first row as its width
		 * is the one the thead first cells should have.
		 * Since we are making the thead floats, it looses
		 * the table capability of having the same size
		 * as the siblings.
		 */
		const firstBodyRow = tbody?.childNodes[0];
		const firstCell = firstBodyRow?.childNodes[0];

		if (!firstCell) {
			return;
		}

		const firstColumnWidth = firstCell.offsetWidth;

		const { left, top } = container.getBoundingClientRect();

		let rowTop = top;

		/*
		 * This if a workaround as the top graph has a strange
		 * size, that we can't really fix with CSS.
		 */
		if (!this._saveSpaceMode) rowTop++;

		/*
		 * We will now give all the thead first cell a fixed
		 * position so that it's sticky in both directions.
		 * To be able to do that, we need to set each cell's top
		 * and left values.
		 * As the save space mode has one row less (the top graph)
		 * we can use the tr height to set the height of the cell,
		 * so that we mimic the table behavior (all cells with same height).
		 * When not in save space mode, we can't use the tr height because
		 * of the top graph (which tr is 10px height oO). So we use the th
		 * instead, but the result is less guaranteed to be exact.
		 * For the other rows (not related to the graph), we can simply
		 * set the first cell height to the height of the next cell.
		 */
		[...thead.childNodes].forEach((tr, index) => {
			const th = tr.childNodes[0];
			th.style.top = `${rowTop}px`;
			th.style.left = `${left}px`;

			th.style.width = `${firstColumnWidth}px`;

			const baseHeight = this._saveSpaceMode
				? tr.offsetHeight
				: th.offsetHeight;

			const height = tr.childNodes[1]?.offsetHeight || baseHeight;

			rowTop += index === 0 ? baseHeight : height;

			if (index === 0 && !this._saveSpaceMode) return;
			th.style.height = `${height}px`;
		});
	}

	_makeReportViewer() {
		return new ReportTableWidget({
			report: this._report,
			headRenderer: this._tableHeadRenderer.bind(this),
			dataCellRenderer: this._dataCellRenderer.bind(this),
			getColumnCssClasses: this._getColumnCssClasses.bind(this),
		});
	}

	_tableHeadRenderer({ columns, columnsTitleRenderer }) {
		return [
			this._chartRowRenderer({ columns }),
			(html) => this._renderSummaryRowOn(html),
			columnsTitleRenderer({
				title: (html) => {
					html.th(html.div((html) => html.raw("&nbsp;"))); // this ensures the line has a consistent height
				},
			}),
		];
	}

	_dataCellRenderer({ row, column, content = "", cssClass }) {
		const cell = { row, column };
		const clickable = !!content;

		let dataHelp;

		if (clickable) {
			if (column.isPredictedForecastColumn()) {
				dataHelp = row.isAccountRow()
					? "predicted-clickable-account-cell"
					: "predicted-clickable-non-account-cell";
			} else if (column.isCurrentPeriodForecastColumn()) {
				dataHelp = "current-period-clickable-cell";
			}
		}

		return (html) => {
			html.td(
				{
					"data-row-id": row.getId(),
					"data-column-id": column.getId(),
					"data-help": dataHelp,
					class: [
						cssClass,
						this._getColumnCssClasses(column),
						{
							clickable,
							selected: clickable && this._isCellSelected(cell),
						},
					],
					click:
						clickable &&
						(() => {
							this.selectCell(cell);
							this._onCellClick(cell);
						}),
				},
				html.span(content),
			);
		};
	}

	_summaryRowLabelRenderer() {
		if (this._saveSpaceMode) {
			return (html) =>
				html.div({ "data-help": "card" }, this._makeFiscalYearCard());
		}

		return this._report.getSummaryRowTitle().toUpperCase();
	}

	_renderSummaryRowOn(html) {
		const visibleColumns = this._report.getVisibleColumns();

		html.tr(
			html.th(
				{
					class: [
						"report-title",
						{
							"save-space": this._saveSpaceMode,
						},
					],
				},
				this._summaryRowLabelRenderer(),
			),
			this._report
				.getKeyFigureValuesForSummaryRow()
				.map((keyFigureValue, index) => {
					const column = visibleColumns[index];
					const value = keyFigureValue.getValue();
					let dataHelp;

					if (column.isCurrentPeriodForecastColumn()) {
						dataHelp = "summary-current-period";
					}

					return (html) =>
						html.td(
							{
								"data-help": dataHelp,
								class: [
									"summary-header",
									this._getColumnCssClasses(column),
									{
										positive: value >= 0,
										negative: value < 0,
									},
								],
							},
							this._getFormattedKeyFigureValue(keyFigureValue),
						);
				}),
		);
	}

	_registerCardUpdateOnScroll() {
		const $this = this.asJQuery();

		const fiscalYearBoundaries = this._buildFiscalYearBoundaries({ $this });

		const stickyColumnWidth = this.asJQuery()
			.find("tr:first-child th")
			.get(0).clientWidth;

		let currentClosingMonthIndex = -1;

		$this
			.find(".reportViewer")
			.get(0)
			.addEventListener("scroll", (event) => {
				const cursorX = event.target.scrollLeft + stickyColumnWidth;

				fiscalYearBoundaries.forEach(
					({ left, right, closingColumnIndex }) => {
						if (
							cursorX < left ||
							cursorX > right ||
							currentClosingMonthIndex === closingColumnIndex
						) {
							return;
						}

						currentClosingMonthIndex = closingColumnIndex;
						this._triggerClosingMonthScroll({ closingColumnIndex });
					},
				);
			});
	}

	_triggerClosingMonthScroll({ closingColumnIndex }) {
		const newValues = this._report.getValuesForCard({
			closingColumnIndex,
		});

		this._currentFiscalYearCard.setValues(newValues);
	}

	_buildFiscalYearBoundaries({ $this }) {
		let left = 0;

		const lastColumn = $this.find(
			"th.predicted-period:not(.is-closing):last-of-type",
		);

		return [...$this.find("th.is-closing"), ...lastColumn].map((column) => {
			const columnLeft = column.offsetLeft;

			const right = columnLeft + column.clientWidth;

			const result = {
				left,
				right,
				closingColumnIndex: column.cellIndex - 1,
			};

			left = right + 1;
			return result;
		});
	}

	_getFormattedKeyFigureValue(keyFigureValue) {
		if (keyFigureValue.isPercentage()) {
			return keyFigureValueFormatter().format(keyFigureValue);
		}

		const formatter = this._report.getDisplaySettings().getFormatter();
		return formatter(keyFigureValue.getValue());
	}

	_chartRowRenderer({ columns }) {
		if (this._saveSpaceMode) return () => {};

		const chart = this._makeChart();

		if (!chart) return (_html) => {};

		return (html) =>
			html.tr(
				{ "data-test": "chart-row" },
				html.th(
					{ class: "title-card", "data-help": "card" },
					this._makeFiscalYearCard(),
				),
				html.th(
					{ colspan: columns.length, "data-help": "chart" },
					chart,
				),
			);
	}

	_makeChart() {
		return new ChartWidget({
			values: this._report.getChartValues(),
			height: "10",
			chartStrategy: this._report.getChartStrategy(),
			dataTestName: this._report.getChartDataTestName(),
		});
	}

	_makeFiscalYearCard() {
		this._currentFiscalYearCard = new ForecastFiscalYearCard({
			renderingStrategy:
				this._report.getFiscalYearCardRenderingStrategy(),
			oneLiner: this._saveSpaceMode,
		});

		return this._currentFiscalYearCard;
	}

	_getColumnCssClasses(column) {
		return [
			this._cssClassNameRenderer.visit(column),
			{ "is-closing": column.isClosing?.() },
			{ "reference-period": column.isReferenceForecastColumn() },
			{ "current-period": column.isCurrentPeriodForecastColumn() },
			{ "predicted-period": column.isPredictedForecastColumn() },
			{ "frozen-period": column.isFrozenPeriodForecastColumn() },
		];
	}

	_isCellSelected({ row, column }) {
		return (
			this._selectedCell.row === row &&
			this._selectedCell.column === column
		);
	}

	/**
	 * Return new horizontal and vertical scroll positions to make
	 * `cell` visible on screen in `reportViewerDOM`. `leftShift`
	 * and `rightShift` represent an amount of pixels of the report
	 * viewer that is covered by something else (e.g., a sticky
	 * column).
	 */
	_computeScrollTarget({
		cell,
		leftShift = 0,
		rightShift = 0,
		reportViewerDOM,
	}) {
		const scrollbarHeight =
			reportViewerDOM.offsetHeight - reportViewerDOM.clientHeight;

		const cellTargetPosition = {
			left: clamp(
				cell.position().left,
				leftShift + safetyScrollMargin,
				reportViewerDOM.clientWidth -
					rightShift -
					safetyScrollMargin -
					cell.get(0).clientWidth,
			),
			top: clamp(
				cell.position().top,
				safetyScrollMargin,
				reportViewerDOM.clientHeight -
					cell.get(0).clientHeight -
					scrollbarHeight -
					safetyScrollMargin,
			),
		};

		return {
			scrollTop:
				reportViewerDOM.scrollTop -
				cellTargetPosition.top +
				cell.position().top,
			scrollLeft:
				reportViewerDOM.scrollLeft -
				cellTargetPosition.left +
				cell.position().left,
		};
	}
}
